import expandTimeZoneDefinition from './expandTimeZoneDefinition';
import type {
    TimeZoneExpansionFailureCause,
    TimeZoneExpansionDiagnostics,
} from './expandTimeZoneDefinition';
import type { TimeZoneRange } from '../TimeZoneRange';
import isTimezoneAvailable from '../selectors/isTimezoneAvailable';
import {
    setCustomTimeZoneRangeMutator,
    removeCustomTimeZoneRangeMutator,
} from '../mutators/timeZoneRangesMutators';
import type TimeZoneDefinitionType from 'owa-service/lib/contract/TimeZoneDefinitionType';

/**
 * Namespaced prefix for synthetic per-series custom zones. Contains only characters that survive
 * `cleanTimeZoneId` unchanged (it is not one of the `tzone://Microsoft/...` special forms) and is
 * never `'UTC'`, so the synthetic id flows through the expansion engine transparently.
 */
export const CUSTOM_TIME_ZONE_PREFIX = 'x-owa-custom-';

const MAX_CUSTOM_ZONES = 200;
// Forward span: the delta sync window is a few months around "now", always inside currentUtcYear ± 1,
// so one warm-up year AFTER the current year covers every occurrence a plan can reference plus the
// expander's look-ahead. Backward span is DIFFERENT: a recurring series' anchor
// (RecurrenceRange.StartDate) is parsed IN the synthetic zone (expandRecurrence.ts) and can be
// historical (e.g. a series created in 2015 still syncing today). If the zone's earliest range does
// not reach back to the anchor's year, the backward flat-extension (timeZoneRanges.ts extends the
// first range's offset to the beginning of time) gives the anchor the WRONG DST offset, which — when
// the anchor's local time sits within ~1h of midnight — can flip its calendar day and corrupt the
// whole series' occurrence dates/parity. So we expand the lower bound down to a fixed historical
// floor and let the group-timeline in the definition apply the correct rule per year. Extra ranges
// are cheap (~2/year × ~200-zone cap), and correctness at any realistic anchor is worth it.
const EARLIEST_REGISTER_YEAR = 1970;
const REGISTER_YEARS_AFTER = 1;
const MS_PER_DAY = 86400000;

interface CachedZone {
    /** The synthetic zone id registered in the store. */
    id: string;
    /** JSON of the source definition — the equality key that guards against hash collisions. */
    serialized: string;
    /** djb2 hash bucket this zone lives in. */
    hash: string;
    /**
     * UTC year the entry's ranges were expanded for (registration `currentUtcYear()`). The ranges
     * cover [historical floor .. that year + 1], so once the current UTC year drifts past this the
     * FORWARD coverage is stale: the final range flat-extends to the max timestamp and later DST
     * transitions never apply. On a cache hit we re-expand (and refresh this) when the year has
     * moved, so a long-lived worker/tab that outlives the registration year stays DST-correct.
     */
    expandedForYear: number;
}

/**
 * LRU keyed by synthetic zone id (least-recently-used id is first). Refreshed on every hit so an
 * in-use zone is never evicted mid-use. Paired with {@link bucketByHash} for O(1) lookup by rule.
 */
const lruById = new Map<string, CachedZone>();

/**
 * Hash-bucket index: djb2(serialized) → the zones sharing that hash. A bucket normally holds one
 * zone; it holds more ONLY on a (astronomically rare) 32-bit hash collision between two DISTINCT
 * definitions, which chaining resolves so colliding rules never share — and overwrite — one zone.
 */
const bucketByHash = new Map<string, CachedZone[]>();

export interface CustomTimeZoneResolution {
    /** The synthetic zone id to use, or `undefined` when the definition could not be expanded. */
    id?: string;
    /** Synthetic zone ids evicted by this call (for eviction telemetry). */
    evicted: string[];
    /**
     * When `id` is undefined because a PRESENT definition failed strict expansion, the specific
     * failure category (see {@link TimeZoneExpansionFailureCause}). Undefined when the definition
     * was absent (no rule to expand) or on success. Lets callers count WHICH definition shapes force
     * server expansion rather than one opaque "unexpandable" bucket.
     */
    unexpandableCause?: TimeZoneExpansionFailureCause;
}

/**
 * Resolve a raw wire `TimeZoneDefinitionType` to a synthetic, registered timezone id whose ranges
 * exactly reflect the series' custom DST rule.
 *
 * Registers the expanded ranges in the datetime store (once per distinct rule, LRU-cached, cap 200)
 * and returns the synthetic id to feed downstream as the series' timezone. On a cache hit the id is
 * refreshed to most-recently-used so it is never evicted mid-use. Returns `id: undefined` when the
 * definition is absent or cannot be expanded, so callers fall back to the promoted named zone.
 *
 * Evicted ids (if any) are surfaced so the caller can aggregate bounded eviction telemetry; the
 * cache itself stays pure (no telemetry / mailbox coupling).
 */
export function getOrRegisterCustomTimeZone(
    definition: TimeZoneDefinitionType | undefined
): CustomTimeZoneResolution {
    const evicted: string[] = [];
    if (!definition) {
        return { id: undefined, evicted };
    }

    const serialized = JSON.stringify(definition);
    const hash = hashDefinition(serialized);
    const currentYear = currentUtcYear();
    const bucket = bucketByHash.get(hash);
    const existing = bucket?.find(z => z.serialized === serialized);
    if (existing) {
        // Refresh recency: delete + re-insert moves the id to the most-recent end.
        lruById.delete(existing.id);
        lruById.set(existing.id, existing);
        // Re-expand when EITHER the store was reset (re-init dropped the ranges) OR the entry's
        // coverage year is stale — a long-lived worker/tab may outlive the year the ranges were
        // expanded for (forward bound = year + 1), after which the final range flat-extends and
        // later DST transitions never apply. Both cases re-expand for the CURRENT year and refresh
        // the stamp.
        if (!isTimezoneAvailable(existing.id) || existing.expandedForYear !== currentYear) {
            const diagnostics: TimeZoneExpansionDiagnostics = {};
            const ranges = expandForYear(definition, currentYear, diagnostics);
            if (!ranges) {
                removeFromCache(existing);
                removeCustomTimeZoneRangeMutator(existing.id);
                return { id: undefined, evicted, unexpandableCause: diagnostics.cause };
            }
            setCustomTimeZoneRangeMutator(existing.id, ranges);
            existing.expandedForYear = currentYear;
        }
        return { id: existing.id, evicted };
    }

    const diagnostics: TimeZoneExpansionDiagnostics = {};
    const ranges = expandForYear(definition, currentYear, diagnostics);
    if (!ranges) {
        return { id: undefined, evicted, unexpandableCause: diagnostics.cause };
    }

    // First zone in a bucket keeps the clean PREFIX+hash id. A collision (distinct definition, same
    // hash) is disambiguated by probing a numeric suffix until an id no colliding zone already uses,
    // so the two rules never share a store id. Probing (not a second hash) is used because a second
    // djb2 hash preserves equality for equal-length collisions — the very case we must separate.
    let id: string;
    const baseId = CUSTOM_TIME_ZONE_PREFIX + hash;
    if (!bucket || bucket.length === 0) {
        id = baseId;
    } else {
        const usedIds = new Set(bucket.map(z => z.id));
        let suffix = 1;
        id = baseId + '-' + suffix;
        while (usedIds.has(id)) {
            suffix++;
            id = baseId + '-' + suffix;
        }
    }
    const zone: CachedZone = { id, serialized, hash, expandedForYear: currentYear };
    if (bucket) {
        bucket.push(zone);
    } else {
        bucketByHash.set(hash, [zone]);
    }
    setCustomTimeZoneRangeMutator(id, ranges);
    lruById.set(id, zone);

    // Evict least-recently-used zones beyond the cap. The just-registered zone is most-recent, so
    // it is never among the evicted.
    while (lruById.size > MAX_CUSTOM_ZONES) {
        const oldest = lruById.values().next().value as CachedZone;
        removeFromCache(oldest);
        removeCustomTimeZoneRangeMutator(oldest.id);
        evicted.push(oldest.id);
    }

    return { id, evicted };
}

/** Remove a zone from BOTH the LRU and its hash bucket (dropping the bucket when it empties). */
function removeFromCache(zone: CachedZone): void {
    lruById.delete(zone.id);
    const bucket = bucketByHash.get(zone.hash);
    if (bucket) {
        const next = bucket.filter(z => z.id !== zone.id);
        if (next.length > 0) {
            bucketByHash.set(zone.hash, next);
        } else {
            bucketByHash.delete(zone.hash);
        }
    }
}

/** Test-only: clear the module-level caches so cases start from a known state. */
export function resetCustomTimeZoneCacheForTest(): void {
    lruById.clear();
    bucketByHash.clear();
}

function expandForYear(
    definition: TimeZoneDefinitionType,
    year: number,
    diagnostics?: TimeZoneExpansionDiagnostics
): TimeZoneRange[] | undefined {
    return expandTimeZoneDefinition(
        definition,
        EARLIEST_REGISTER_YEAR,
        year + REGISTER_YEARS_AFTER,
        diagnostics
    );
}

/** djb2 hash of the definition's JSON, base-36 encoded. Stable for identical wire rules. */
function hashDefinition(serialized: string): string {
    let hash = 5381;
    for (let i = 0; i < serialized.length; i++) {
        hash = ((hash << 5) + hash + serialized.charCodeAt(i)) | 0;
    }
    return (hash >>> 0).toString(36);
}

/**
 * Current UTC year via the civil-from-days algorithm on `Date.now()` — pure integer math, no `Date`
 * instance. Used only to pick the registration's current-year upper bound (expansion runs through
 * the following year), so it need not be exact to the day; that forward warm-up year absorbs any
 * boundary imprecision.
 */
function currentUtcYear(): number {
    const z = Math.floor(Date.now() / MS_PER_DAY) + 719468;
    const era = Math.floor((z >= 0 ? z : z - 146096) / 146097);
    const doe = z - era * 146097;
    const yoe = Math.floor(
        (doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365
    );
    const year = yoe + era * 400;
    const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
    const monthPrime = Math.floor((5 * doy + 2) / 153);
    const month = monthPrime < 10 ? monthPrime + 3 : monthPrime - 9;
    return month <= 2 ? year + 1 : year;
}
